import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminShell from '../shell/AdminShell';
import ConfirmModal from '../../ConfirmModal';
import PermissionRail from './PermissionRail';
import PermissionAreaCard from './PermissionAreaCard';
import { summarize, sameSet, withImplied } from './permissionModel';
import { getPermissionCatalog, getRole, createRole, updateRole, deleteRole } from './rolesApi';
import { toastSuccess, toastError, errorMessage } from '../../../lib/toast';
import './roleEditor.css';

const ROLE_ERRORS = {
  duplicate_key: 'A role with that name already exists.',
  reserved_key: 'That name is reserved for a system role.',
  label_too_long: 'Role names can be at most 100 characters.',
  unknown_permission: 'One or more selected permissions are invalid.',
  admin_permissions_locked: 'The Administrator role always has every permission.',
  locked_permission: 'That permission can only belong to administrators.',
  system_role: 'System roles cannot be deleted.',
};

export default function RoleEditorPage() {
  const { roleId } = useParams();
  const isNew = roleId === 'new';
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [role, setRole] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error | missing
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [saved, setSaved] = useState({ label: '', description: '', perms: new Set() });
  const [query, setQuery] = useState('');
  const [labelError, setLabelError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'delete' | 'leave'

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const [cat, r] = await Promise.all([getPermissionCatalog(), isNew ? Promise.resolve(null) : getRole(roleId)]);
      const allPerms = cat.areas.flatMap((a) => a.permissions);
      // Normalise the loaded set so a legacy role holding only the implying key
      // (e.g. sync.user) shows its cross-area implied key (users.view) ticked
      // and locked; the normalised set is also the "saved" baseline so the
      // form is not dirty on load.
      const perms = withImplied(new Set(r ? r.permissions : []), allPerms);
      setCatalog(cat);
      setRole(r);
      setLabel(r ? r.label : '');
      setDescription(r ? r.description || '' : '');
      setSelected(perms);
      setSaved({ label: r ? r.label : '', description: r ? r.description || '' : '', perms });
      setStatus('ready');
    } catch (err) {
      setStatus(err?.response?.status === 404 ? 'missing' : 'error');
    }
  }, [roleId, isNew]);

  useEffect(() => { load(); }, [load]);

  const isAdminRole = role?.key === 'admin';
  const dirty = !isAdminRole && (label !== saved.label || description !== saved.description || !sameSet(selected, saved.perms));

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const summary = useMemo(() => (catalog ? summarize(catalog, selected) : null), [catalog, selected]);
  const allPermissions = useMemo(() => (catalog ? catalog.areas.flatMap((a) => a.permissions) : []), [catalog]);

  const save = async () => {
    if (!label.trim()) { setLabelError('Name is required.'); return; }
    setSaving(true);
    const payload = { label: label.trim(), description: description.trim(), permissions: [...selected] };
    try {
      if (isNew) {
        const created = await createRole(payload);
        toastSuccess('Role created.');
        navigate(`/admin/roles/${created.id}`, { replace: true });
      } else {
        await updateRole(role.id, payload);
        toastSuccess('Role saved.');
        setSaved({ label: payload.label, description: payload.description, perms: new Set(selected) });
      }
    } catch (err) {
      toastError(errorMessage(err, "Couldn't save the role.", ROLE_ERRORS));
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setLabel(saved.label); setDescription(saved.description); setSelected(new Set(saved.perms)); setLabelError('');
  };

  const doDelete = async () => {
    setConfirm(null);
    try {
      await deleteRole(role.id);
      toastSuccess('Role deleted.');
      navigate('/admin/roles');
    } catch (err) {
      toastError(errorMessage(err, "Couldn't delete the role.", ROLE_ERRORS));
    }
  };

  const onBack = (e) => {
    if (!dirty) return;
    e.preventDefault();
    setConfirm('leave');
  };

  const jump = (key) => {
    const el = document.getElementById(`re-area-${key}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const title = isNew ? 'New role' : role?.label || 'Role';

  let body;
  if (status === 'loading') {
    body = <div className="re-skeleton" aria-busy="true" aria-label="Loading role"><div /><div /><div /></div>;
  } else if (status === 'error') {
    body = <div className="re-state">Couldn't load this role. <button type="button" className="btn btn-secondary" onClick={load}>Retry</button></div>;
  } else if (status === 'missing') {
    body = <div className="re-state">This role doesn't exist. <Link to="/admin/roles">Back to roles</Link></div>;
  } else {
    const visibleAreas = catalog.areas;
    const anyMatch = !query || visibleAreas.some((a) => a.permissions.some((p) => `${p.label} ${p.description}`.toLowerCase().includes(query.trim().toLowerCase())));
    body = (
      <div className="re-page">
        <div className="re-head">
          <nav className="re-crumbs" aria-label="Breadcrumb">
            <Link to="/admin/roles" onClick={onBack}>Roles</Link> / <span>{title}</span>
            {role?.is_system && <span className="rbac-badge">System role</span>}
          </nav>
          <div className="re-fields">
            <div className="form-group">
              <label htmlFor="re-name">Name</label>
              <input id="re-name" value={label} disabled={role?.is_system} maxLength={100}
                onChange={(e) => { setLabel(e.target.value); setLabelError(''); }} />
              {labelError && <p className="field-error">{labelError}</p>}
            </div>
            <div className="form-group">
              <label htmlFor="re-desc">Description</label>
              <input id="re-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </div>
          {isAdminRole && <p className="re-notice">Administrators have every permission, including the ones that can't be granted.</p>}
          {role?.key === 'dept_manager' && <p className="re-notice">Managers also keep their built-in powers over the departments they manage.</p>}
        </div>

        <div className="re-body">
          <PermissionRail areas={visibleAreas} selected={selected} query={query} onQuery={setQuery} onJump={jump} />
          <div className="re-cards">
            {!anyMatch && <p className="re-state">{`No permissions match "${query}".`}</p>}
            {visibleAreas.map((a) => (
              <PermissionAreaCard key={a.key} area={a} selected={selected} onChange={setSelected} readOnly={isAdminRole} query={query} allPermissions={allPermissions} />
            ))}
            <section className="re-card re-locked" aria-label="Only full administrators">
              <h3 className="re-card-title">Only full administrators</h3>
              <p className="re-card-summary">{catalog.locked.join(' · ')}</p>
            </section>
          </div>
        </div>

        <footer className="re-foot">
          <div className="re-foot-summary">
            {selected.size === 0 && !isAdminRole && <p className="re-warn">This role grants no access.</p>}
            <p>
              {summary.destinations.length ? `This role can open: ${summary.destinations.join(', ')} · ` : ''}
              {`${summary.count} of ${summary.total} permissions`}
            </p>
          </div>
          <div className="re-foot-actions">
            {!isNew && !role?.is_system && (
              <button type="button" className="btn btn-danger" onClick={() => setConfirm('delete')}>Delete role</button>
            )}
            <button type="button" className="btn btn-secondary" onClick={discard} disabled={!dirty || saving}>Discard</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={saving || isAdminRole || !dirty}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <AdminShell activeKey="roles" title={title}>
      {body}
      <div role={confirm ? 'dialog' : undefined}>
        <ConfirmModal
          isOpen={confirm === 'delete'}
          title={`Delete ${role?.label || 'role'}?`}
          message={role?.user_count ? `${role.user_count} people hold this role and will lose the access it grants.` : 'Nobody holds this role yet.'}
          confirmText="Delete role"
          isDangerous
          onConfirm={doDelete}
          onCancel={() => setConfirm(null)}
        />
        <ConfirmModal
          isOpen={confirm === 'leave'}
          title="Discard unsaved changes to this role?"
          message="Your changes to this role have not been saved."
          confirmText="Discard"
          isDangerous
          onConfirm={() => { setConfirm(null); navigate('/admin/roles'); }}
          onCancel={() => setConfirm(null)}
        />
      </div>
    </AdminShell>
  );
}
